import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useUser } from '../../Hooks/useUser';
import axios from "../../api/axios";

const Education = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user: contextUser } = useUser();
  const user = props.User ?? contextUser;

  const [bomEducation, setBomEducation] = useState({
    BPEducationID: 0,
    BP_UserGUID: user?.UserGUID,
    BP_E_School: '',
    BP_E_Degree: '',
    BP_E_Field: '',
    BP_E_YearsFrom: '',
    BP_E_YearsTo: '',
  });

  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorFields, setErrorFields] = useState({});

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [editMode, setEditMode] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [educationToDelete, setEducationToDelete] = useState(null);
  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setEducationToDelete(null);
  };

  const resetEducation = useCallback(() => {
    setBomEducation({
      BPEducationID: 0,
      BP_UserGUID: user?.UserGUID,
      BP_E_School: '',
      BP_E_Degree: '',
      BP_E_Field: '',
      BP_E_YearsFrom: '',
      BP_E_YearsTo: '',
    });
  }, [user?.UserGUID]);

  // Keep BP_UserGUID synced in case user resolves after mount (admin mode)
  useEffect(() => {
    setBomEducation(prev => ({ ...prev, BP_UserGUID: user?.UserGUID }));
  }, [user?.UserGUID]);

  const handleAdd = () => {
    setEditMode(false);
    setErrorFields({});
    resetEducation();
    setOpen(true);
  };

  const handleEdit = (obj, params) => {
    setEditMode(true);
    setErrorFields({});
    setOpen(true);
  };

  const handleDeleteClick = (ob, params) => {
    setEducationToDelete(params.row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!educationToDelete) return;

    axios.delete(`/api/educations/${educationToDelete["BPEducationID"]}`)
      .then(() => {
        enqueueSnackbar("Education Deleted Successfully");
        getData();
        handleDeleteConfirmClose();
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(err?.response?.data?.error || "Education Delete Failed", { variant: 'error' });
        handleDeleteConfirmClose();
      });
  };

  function handleFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setBomEducation(prev => ({ ...prev, [name]: value }));
    if (errorFields[name]) {
      setErrorFields({ ...errorFields, [name]: false });
    }
  }

  const handleSubmit = async e => {
    e.preventDefault();

    const errors = {};
    if (!bomEducation.BP_E_School?.trim()) {
      errors.BP_E_School = true;
      enqueueSnackbar("Please enter school name", { variant: 'error' });
    }
    if (!bomEducation.BP_E_Field?.trim()) {
      errors.BP_E_Field = true;
      enqueueSnackbar("Please enter field of study", { variant: 'error' });
    }
    if (!bomEducation.BP_E_Degree?.trim()) {
      errors.BP_E_Degree = true;
      enqueueSnackbar("Please enter degree", { variant: 'error' });
    }
    if (!bomEducation.BP_E_YearsFrom) {
      errors.BP_E_YearsFrom = true;
      enqueueSnackbar("Please select start year", { variant: 'error' });
    }
    if (!bomEducation.BP_E_YearsTo) {
      errors.BP_E_YearsTo = true;
      enqueueSnackbar("Please select end year", { variant: 'error' });
    }

    if (Object.keys(errors).length > 0) {
      setErrorFields(errors);
      return;
    }
    setErrorFields({});
    setSaving(true);

    const request = editMode
      ? axios.put('/api/educations', bomEducation)
      : axios.post('/api/educations', bomEducation);

    try {
      await request;
      enqueueSnackbar(editMode ? "Education Updated" : "Education Saved");
      setTimeout(() => getData(), 300);
      handleClose();
      resetEducation();
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar(
        (editMode ? "Education Update failed: " : "Education Save Failed: ") + (ex.response?.data?.message || ex.message),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const columns = [
    { field: 'BP_E_School', headerName: 'School', flex: 1.5, minWidth: 150 },
    { field: 'BP_E_Degree', headerName: 'Degree', flex: 1, minWidth: 120 },
    { field: 'BP_E_Field', headerName: 'Field of Study', flex: 1.5, minWidth: 150 },
    { field: 'BP_E_YearsFrom', headerName: 'Start Year', flex: 0.8, minWidth: 100 },
    { field: 'BP_E_YearsTo', headerName: 'End Year', flex: 0.8, minWidth: 100 },
    {
      field: "Actions", flex: 1.5, minWidth: 200,
      renderCell: (cellValues) => (
        <div className="d-flex gap-2">
          <Button
            variant="contained"
            color="primary"
            onClick={(event) => {
              setBomEducation(cellValues.row);
              handleEdit(event, cellValues);
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={(event) => handleDeleteClick(event, cellValues)}
          >
            Delete
          </Button>
        </div>
      )
    },
  ];

  const getData = useCallback(() => {
    if (!user?.UserGUID) return;

    setLoading(true);
    axios.get(`/api/educations/${user.UserGUID}`)
      .then((response) => {
        setEducations(response.data);
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar('Failed to load education history', { variant: 'error' });
      })
      .finally(() => setLoading(false));
  }, [user?.UserGUID, enqueueSnackbar]);

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 80;
    const years = [];
    for (let year = currentYear; year >= startYear; year--) years.push(year);
    return years;
  };
  const yearsList = generateYears();

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <div id="tab3">
      <div className="row px-5 pt-5">
        <div className="col-md-12">
          <DataGrid
            flexGrow
            autoHeight={true}
            autoPageSize={true}
            pageSize={10}
            columns={columns}
            rows={educations}
            getRowId={(row) => row.BPEducationID}
            disableMultipleSelection={true}
            disableSelectionOnClick={true}
            loading={loading}
          />
        </div>

        <div className="col-md-12"> <a data-toggle="collapse" onClick={handleAdd} role="button" aria-expanded="false" aria-controls="add-skills" className="button-default collapsed">Add Education</a>
          <div className="collapse" id="add-ed">
            <Modal open={open} onClose={handleClose} aria-labelledby='title' aria-describedby=''>
              <Box sx={modalStyle}>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography id="title" variant='h5'>Education</Typography>
                <div className="row pt-3">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption">School Name<span className="required-field">*</span></label>
                      <input type="text" name='BP_E_School' id="BP_E_School" maxLength="200" className={`form-control ${errorFields.BP_E_School ? 'error-field' : ''}`} value={bomEducation.BP_E_School} placeholder="School Name" onChange={handleFieldChange} />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption">Field of Study<span className="required-field">*</span></label>
                      <input type="text" name='BP_E_Field' id="BP_E_Field" maxLength="200" className={`form-control ${errorFields.BP_E_Field ? 'error-field' : ''}`} value={bomEducation.BP_E_Field} placeholder="Field of Study" onChange={handleFieldChange} />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption">Degree<span className="required-field">*</span></label>
                      <input type="text" name='BP_E_Degree' id="BP_E_Degree" maxLength="200" className={`form-control ${errorFields.BP_E_Degree ? 'error-field' : ''}`} value={bomEducation.BP_E_Degree} placeholder="Degree" onChange={handleFieldChange} />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption" htmlFor="BP_E_YearsFrom">Start Year<span className="required-field">*</span></label>
                      <select name="BP_E_YearsFrom" id="BP_E_YearsFrom" className={`DropDownField form-control ${errorFields.BP_E_YearsFrom ? 'error-field' : ''}`} value={bomEducation.BP_E_YearsFrom} onChange={handleFieldChange}>
                        <option value="">Please select</option>
                        {yearsList.map((year) => <option key={year} value={year}>{year}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="required-caption" htmlFor="BP_E_YearsTo">End Year<span className="required-field">*</span></label>
                      <select name="BP_E_YearsTo" id="BP_E_YearsTo" className={`DropDownField form-control ${errorFields.BP_E_YearsTo ? 'error-field' : ''}`} value={bomEducation.BP_E_YearsTo} onChange={handleFieldChange}>
                        <option value="">Please select</option>
                        {yearsList.map((year) => <option key={year} value={year}>{year}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6 text-white py-1 d-flex gap-2 mt-3">
                    <a data-toggle="collapse" role="button" aria-expanded="false" onClick={handleSubmit} aria-controls="save-skills" className="button-default button-green collapsed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                      {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
                      Save
                    </a>
                    <a data-toggle="collapse" onClick={handleClose} role="button" aria-expanded="false" aria-controls="cancel-skills" className="button-default button-gray collapsed">Cancel</a>
                  </div>
                  <div className="col-md-6 text-white py-1 d-flex gap-2 justify-content-end mt-3">
                    {editMode && <a data-toggle="collapse" role="button" aria-expanded="false" aria-controls="delete-skills" className="button-default button-red collapsed">Delete</a>}
                  </div>
                </div>
              </Box>
            </Modal>
          </div>
        </div>

        <Modal open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} aria-labelledby='delete-title' aria-describedby='delete-description'>
          <Box sx={modalStyle}>
            <IconButton
              aria-label="close"
              onClick={handleDeleteConfirmClose}
              sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
            <Typography id="delete-title" variant='h5' sx={{ mb: 2 }}>Confirm Delete</Typography>
            <Typography id="delete-description" variant='body1' sx={{ mb: 4 }}>
              Are you sure you want to delete this entry? This action cannot be undone.
            </Typography>
            <div className="row">
              <div className="col-md-12 text-white py-1 d-flex gap-2">
                <a role="button" onClick={handleDeleteConfirm} className="button-default button-red collapsed">Delete</a>
                <a role="button" onClick={handleDeleteConfirmClose} className="button-default button-gray collapsed">Cancel</a>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
};
export default Education;